import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CaptainWidget,
  VirtualCaptainProvider,
  resolveWelcomeMessage,
  resolveMenuBrowseNudge,
  resolvePageNudgeMessage,
  resolveItemStatusMessage,
  resolveOrderStatusMessage,
  resolveTableReadyMessage,
  resolveQueueReadyMessage,
  resolveQueueStatusMessage,
  resolveAssistanceMessage,
  resolveCallCaptainMessage,
  resolveWaitTimeMessage,
  useCaptainTTS,
  DINE_IN_ORDER_TYPE,
  isAssistanceEligibleStatus,
} from '../../virtual-captain';
import type { CaptainMessage, OngoingOrderSnapshot } from '../../virtual-captain/types/events';
import '../../virtual-captain/styles/captain.css';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setCaptainVoiceEnabled } from '../../store/slices/userSlice';
import authService from '../../services/authService';
import { useRealtime } from '../../contexts/RealtimeContext';
import restaurantService, { UserOrderDetails } from '../../services/restaurantService';
import { hasCustomerFeature, CUSTOMER_FEATURE_QUICK_ASSISTANCE, PlanFeaturesPayload } from '../../utils/planFeatures';
import {
  extractAppNameFromPath,
  extractOrderIdFromPath,
  isRestaurantCustomerRoute,
  isReserveTableRoute,
} from '../../utils/restaurantRoutes';
import { resolveAiCaptainPageContext } from '../../utils/aiCaptainPageContext';
import {
  hasRestaurantWelcome,
  markRestaurantWelcomed,
  hasPageNudge,
  markPageNudge,
  hasAnnouncedOrderStatus,
  markAnnouncedOrderStatus,
  hasAnnouncedItemStatus,
  markAnnouncedItemStatus,
  hasAnnouncedWaitTime,
  markAnnouncedWaitTime,
  isVoiceTipDismissed,
  markVoiceTipDismissed,
  markAiCaptainPageVisited,
  hasAiCaptainPageVisited,
} from '../../utils/captainSessionMemory';
import {
  ORDER_PLACED_EVENT,
  readOngoingOrderContext,
  saveOngoingOrderContext,
  clearOngoingOrderContext,
} from '../../utils/ongoingOrderContext';
import { isTerminalOrderStatus } from '../../utils/orderStatus';
import { nudgeCaptainAlert, requestCaptainAlertPermission } from '../../utils/captainAlerts';
import { notifyCaptainTableApproved, notifyCaptainQueueUpdated } from '../../utils/captainPanelEvents';
import useOrderPolling from '../../hooks/useOrderPolling';
import useAiCaptainSession from '../../hooks/useAiCaptainSession';

function normalizePageNudgeKey(pageKey?: string): string {
  if (!pageKey?.trim()) return `ai-${Date.now()}`;
  const colon = pageKey.indexOf(':');
  if (colon < 0) return pageKey;
  const page = pageKey.slice(0, colon);
  const contextJson = pageKey.slice(colon + 1);
  if (page === 'item_detail') {
    try {
      const context = JSON.parse(contextJson) as { itemUrl?: string };
      if (context.itemUrl) {
        return `item_detail:${context.itemUrl}`;
      }
    } catch {
      // fall through
    }
  }
  return page;
}

function buildAiCaptainPageKey(pathname: string): string | null {
  const pageCtx = resolveAiCaptainPageContext(pathname);
  if (!pageCtx) return null;
  return `${pageCtx.page}:${JSON.stringify(pageCtx.context)}`;
}

function mapOrderToSnapshot(order: {
  orderId: number;
  userOrder: UserOrderDetails['userOrder'];
  userOrderItemsList?: UserOrderDetails['userOrderItemsList'];
  userRestaurantTable?: UserOrderDetails['userRestaurantTable'];
}): OngoingOrderSnapshot {
  const uo = order.userOrder;
  return {
    orderId: order.orderId,
    orderStatus: uo.orderStatus,
    orderType: uo.orderType,
    orderCode: uo.orderCode,
    tableNumber: uo.tableNumber ?? order.userRestaurantTable?.tableNumber,
    spaceNoun: uo.spaceNoun ?? order.userRestaurantTable?.spaceNoun,
    waitTimeMinutes: uo.approxWaitTime,
    items: (order.userOrderItemsList || []).map((item) => ({
      name: item.itemName || 'Item',
      quantity: item.quantity || 1,
    })),
  };
}

const MaznutCaptainBridge: React.FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAppSelector((state) => state.user);
  const { subscribeOrderStatus, subscribeTableRequestStatus, subscribeOrderWaitTime, subscribeQueueTokenUpdates } = useRealtime();

  const persistVoicePreference = useCallback(
    async (enabled: boolean) => {
      if (enabled) {
        await requestCaptainAlertPermission();
      }
      const response = await authService.updateCaptainVoicePreference(enabled);
      if (response.success && response.user) {
        dispatch(setCaptainVoiceEnabled(!!response.user.captainVoiceEnabled));
      }
    },
    [dispatch]
  );

  const { voiceEnabled, setVoiceEnabled, unlockVoice, speak } = useCaptainTTS({
    persistedEnabled: user != null ? !!user.captainVoiceEnabled : null,
    onPersist: isAuthenticated ? persistVoicePreference : undefined,
  });

  const pathAppName = extractAppNameFromPath(location.pathname);
  const orderIdFromPath = useMemo(
    () => extractOrderIdFromPath(location.pathname),
    [location.pathname]
  );
  const routeEligible = isRestaurantCustomerRoute(location.pathname);
  const reserveTableRoute = isReserveTableRoute(location.pathname);

  const [appName, setAppName] = useState<string | null>(null);
  const [restaurantId, setRestaurantId] = useState<string>('');
  const [restaurantName, setRestaurantName] = useState('');
  const [features, setFeatures] = useState<PlanFeaturesPayload | undefined>();
  const [ongoingOrder, setOngoingOrder] = useState<OngoingOrderSnapshot | null>(null);
  const [pendingOrderId, setPendingOrderId] = useState<number | null>(() => {
    const ctx = readOngoingOrderContext();
    return ctx?.orderId ?? null;
  });
  const [messages, setMessages] = useState<CaptainMessage[]>([]);
  const [showVoiceTip, setShowVoiceTip] = useState(() => !isVoiceTipDismissed());
  const [metaReady, setMetaReady] = useState(false);
  const [orderReady, setOrderReady] = useState(false);
  const [tableAllocated, setTableAllocated] = useState(false);
  const [allocatedTableNumber, setAllocatedTableNumber] = useState<number | undefined>();
  const lastStatusRef = useRef<string | null>(null);
  const welcomedAppRef = useRef<string | null>(null);
  const displayedOrderStatusRef = useRef<string | null>(null);
  const hydratedDisplayRef = useRef(false);
  const ongoingOrderRef = useRef<OngoingOrderSnapshot | null>(null);
  const restaurantNameRef = useRef('');
  const appNameRef = useRef<string | null>(null);

  ongoingOrderRef.current = ongoingOrder;
  restaurantNameRef.current = restaurantName;
  appNameRef.current = appName;

  const trackedOrderId =
    ongoingOrder?.orderId ??
    orderIdFromPath ??
    pendingOrderId ??
    readOngoingOrderContext()?.orderId ??
    null;

  useEffect(() => {
    if (user?.captainVoiceEnabled) {
      setShowVoiceTip(false);
      markVoiceTipDismissed();
    }
  }, [user?.captainVoiceEnabled]);

  const virtualCaptainEnabled = useMemo(
    () => hasCustomerFeature(features, 'virtual_captain'),
    [features]
  );

  // Widget renders for all authenticated customers on restaurant routes — no feature gate.
  const widgetActive =
    routeEligible && !reserveTableRoute && isAuthenticated && Boolean(appName);

  // Keep `active` as an alias consumed by CaptainWidget (reads it from context).
  const active = widgetActive;

  // Quick Assistance is universally available — no plan gate required.
  // The quick_assistance catalog entry exists for future admin control; for now all restaurants get it.
  const assistanceEnabled = widgetActive;

  const assistanceAvailable = Boolean(
    assistanceEnabled && (
      // Pre-order dine-in: table allocated but no order yet
      tableAllocated ||
      // Any order type with an active order in eligible status
      (ongoingOrder &&
        isAssistanceEligibleStatus(ongoingOrder.orderStatus) &&
        !isTerminalOrderStatus(ongoingOrder.orderStatus))
    )
  );

  const pushMessage = useCallback(
    (message: CaptainMessage, speakText?: string) => {
      setMessages((prev) => {
        if (prev.some((m) => m.id === message.id)) return prev;
        const duplicate = prev.find((m) => m.type === message.type && m.text === message.text);
        if (duplicate) return prev;
        return [...prev, message];
      });
      if (speakText && voiceEnabled) {
        nudgeCaptainAlert({
          title: restaurantNameRef.current || 'Your order',
          body: message.text,
          speakText,
          voiceEnabled,
          speak,
          tag: message.id,
        });
      }
    },
    [speak, voiceEnabled]
  );

  const pushStatusIfChanged = useCallback(
    (
      status: string,
      order: OngoingOrderSnapshot,
      name: string,
      opts?: { displayOnly?: boolean }
    ) => {
      const statusKey = `${order.orderId}:${status}`;
      if (!opts?.displayOnly) {
        if (lastStatusRef.current === statusKey) return;
        if (hasAnnouncedOrderStatus(order.orderId, status)) {
          lastStatusRef.current = statusKey;
          return;
        }
        lastStatusRef.current = statusKey;
        markAnnouncedOrderStatus(order.orderId, status);
      }
      const resolved = resolveOrderStatusMessage(status, order, name);
      if (resolved) {
        pushMessage(
          resolved.message,
          opts?.displayOnly ? undefined : resolved.speakText
        );
      }
    },
    [pushMessage]
  );

  const pushItemStatusIfChanged = useCallback(
    (
      orderItemId: number,
      itemName: string,
      status: string,
      quantity = 1
    ) => {
      if (hasAnnouncedItemStatus(orderItemId, status)) return;
      const resolved = resolveItemStatusMessage(orderItemId, itemName, status, quantity);
      if (!resolved) return;
      markAnnouncedItemStatus(orderItemId, status);
      pushMessage(resolved.message, resolved.speakText);
    },
    [pushMessage]
  );

  const handlePageNudge = useCallback(
    (text: string, speakText?: string, pageKey?: string) => {
      const targetApp = appNameRef.current;
      if (!targetApp || !text.trim()) return;
      const key = normalizePageNudgeKey(pageKey);
      if (hasPageNudge(targetApp, key)) return;
      markPageNudge(targetApp, key);
      const { message } = resolvePageNudgeMessage(key, text.trim());
      pushMessage(message, speakText ?? text.trim());
    },
    [pushMessage]
  );

  const resolveAppName = useCallback((): string | null => {
    if (pathAppName) return pathAppName;
    return readOngoingOrderContext()?.appName ?? null;
  }, [pathAppName]);

  const loadRestaurantMeta = useCallback(async (targetApp: string) => {
    setMetaReady(false);
    try {
      const res = await restaurantService.getRestaurant(targetApp);
      if (res.success && res.restaurant) {
        setRestaurantName(res.restaurant.restaurantName || targetApp);
        setRestaurantId(String(res.restaurant.id));
        setFeatures(res.features);
      }
    } catch {
      setRestaurantName(targetApp);
    } finally {
      setMetaReady(true);
    }
  }, []);

  const loadOrderSnapshotById = useCallback(async (targetApp: string, orderId: number) => {
    try {
      const response = await restaurantService.getOrderDetails(targetApp, orderId);
      if (!response.success || !response.orderDetails) return null;
      const status = response.orderDetails.userOrder?.orderStatus;
      if (status && isTerminalOrderStatus(status)) return null;
      return mapOrderToSnapshot(response.orderDetails);
    } catch {
      return null;
    }
  }, []);

  const loadOngoingOrder = useCallback(async (options?: { silent?: boolean }) => {
    const targetApp = resolveAppName();
    if (!targetApp || !isAuthenticated) {
      setOngoingOrder(null);
      setPendingOrderId(null);
      if (!options?.silent) {
        setOrderReady(true);
      }
      return;
    }
    setAppName(targetApp);
    if (!options?.silent) {
      setOrderReady(false);
    }

    try {
      const response = await restaurantService.getOngoingOrder(targetApp);
      if (response.success && response.hasOngoingOrder && response.order) {
        const status = response.order.userOrder?.orderStatus;
        if (status && isTerminalOrderStatus(status)) {
          clearOngoingOrderContext();
          setOngoingOrder(null);
          setPendingOrderId(null);
          return;
        }
        const snapshot = mapOrderToSnapshot(response.order);
        setOngoingOrder(snapshot);
        setPendingOrderId(snapshot.orderId);
        saveOngoingOrderContext({
          appName: targetApp,
          orderId: response.order.orderId,
          restaurantName,
        });
        return;
      }

      if (orderIdFromPath) {
        const snapshot = await loadOrderSnapshotById(targetApp, orderIdFromPath);
        if (snapshot) {
          setOngoingOrder(snapshot);
          setPendingOrderId(snapshot.orderId);
          saveOngoingOrderContext({
            appName: targetApp,
            orderId: snapshot.orderId,
            restaurantName,
          });
          return;
        }
      }

      setOngoingOrder(null);
      setPendingOrderId(null);
    } catch {
      setOngoingOrder(null);
    } finally {
      if (!options?.silent) {
        setOrderReady(true);
      }
    }
  }, [resolveAppName, isAuthenticated, restaurantName, orderIdFromPath, loadOrderSnapshotById]);

  const pollOngoingOrderSilent = useCallback(() => {
    void loadOngoingOrder({ silent: true });
  }, [loadOngoingOrder]);

  useOrderPolling(
    pollOngoingOrderSilent,
    routeEligible && isAuthenticated && trackedOrderId != null
  );

  const speakAiReply = useCallback(
    (text: string) => {
      if (voiceEnabled) {
        speak(text);
      }
    },
    [speak, voiceEnabled]
  );

  const {
    connected: chatConnected,
    connectionFailed: chatConnectionFailed,
    chatEnabled,
    chatMessages,
    streamingText,
    streamingMenuItems,
    streamingTableItems,
    streamingDealItems,
    sendUserMessage,
    submitChoiceSelection,
    addMenuItemToCart,
  } = useAiCaptainSession({
    enabled: widgetActive && virtualCaptainEnabled && Boolean(appName) && Boolean(restaurantId),
    appName,
    restaurantId,
    userId: user?.id ?? null,
    tableId: ongoingOrder?.tableNumber != null ? String(ongoingOrder.tableNumber) : null,
    orderId: ongoingOrder?.orderId ?? null,
    pathname: location.pathname,
    onSpeak: speakAiReply,
    onPageNudge: handlePageNudge,
  });

  useEffect(() => {
    if (!routeEligible || !isAuthenticated) {
      setAppName(null);
      setOngoingOrder(null);
      setPendingOrderId(null);
      setMessages([]);
      welcomedAppRef.current = null;
      lastStatusRef.current = null;
      displayedOrderStatusRef.current = null;
      hydratedDisplayRef.current = false;
      setMetaReady(false);
      setOrderReady(false);
      setTableAllocated(false);
      setAllocatedTableNumber(undefined);
      return;
    }
    const targetApp = resolveAppName();
    if (!targetApp) return;
    if (welcomedAppRef.current && welcomedAppRef.current !== targetApp) {
      welcomedAppRef.current = null;
    }
    setAppName(targetApp);
    loadRestaurantMeta(targetApp);
    loadOngoingOrder();
    // Check if user already has an allocated table (returning user / page refresh)
    void restaurantService.getDineInTableStatus(targetApp).then((res) => {
      if (res.hasTable && res.dineInTable?.tableNumber != null) {
        setTableAllocated(true);
        setAllocatedTableNumber(Number(res.dineInTable.tableNumber));
      }
    }).catch(() => { /* silent — table status is best-effort */ });
  }, [routeEligible, isAuthenticated, location.pathname, resolveAppName, loadRestaurantMeta, loadOngoingOrder]);

  useEffect(() => {
    if (!active || !restaurantName || !metaReady || !orderReady || !appName) return;

    if (ongoingOrder) {
      const statusKey = `${ongoingOrder.orderId}:${ongoingOrder.orderStatus}`;
      if (displayedOrderStatusRef.current !== statusKey) {
        displayedOrderStatusRef.current = statusKey;
        pushStatusIfChanged(ongoingOrder.orderStatus, ongoingOrder, restaurantName, {
          displayOnly: true,
        });
      }
      return;
    }

    const skipLocalProactive = chatEnabled && !chatConnectionFailed;
    const pageCtx = resolveAiCaptainPageContext(location.pathname);
    const onMenuList = pageCtx?.page === 'menu_list';
    const aiPageKey = buildAiCaptainPageKey(location.pathname);

    if (!skipLocalProactive) {
      if (!hasRestaurantWelcome(appName) && welcomedAppRef.current !== appName) {
        welcomedAppRef.current = appName;
        markRestaurantWelcomed(appName);
        if (aiPageKey) {
          markAiCaptainPageVisited(aiPageKey);
        }
        const { message, speakText } = resolveWelcomeMessage(restaurantName, appName);
        pushMessage(message, speakText);
        return;
      }

      if (onMenuList && !hasPageNudge(appName, 'menu_list')) {
        markPageNudge(appName, 'menu_list');
        if (aiPageKey) {
          markAiCaptainPageVisited(aiPageKey);
        }
        const { message, speakText } = resolveMenuBrowseNudge(appName);
        pushMessage(message, speakText);
      }
      return;
    }

    if (!hydratedDisplayRef.current) {
      const pageVisited =
        (aiPageKey && hasAiCaptainPageVisited(aiPageKey)) || hasRestaurantWelcome(appName);
      if (pageVisited) {
        hydratedDisplayRef.current = true;
        const { message } = resolveWelcomeMessage(restaurantName, appName);
        pushMessage(message);
      }
    }
  }, [
    active,
    appName,
    restaurantName,
    metaReady,
    orderReady,
    ongoingOrder,
    location.pathname,
    pushMessage,
    pushStatusIfChanged,
    chatEnabled,
    chatConnectionFailed,
  ]);

  useEffect(() => {
    if (!trackedOrderId) return undefined;

    return subscribeOrderStatus((payload) => {
      if (Number(payload.orderId) !== trackedOrderId) return;

      if (payload.isItemStatusChange && payload.announce !== false
          && payload.orderItemId && payload.itemName && payload.itemStatus) {
        pushItemStatusIfChanged(
          payload.orderItemId,
          payload.itemName,
          payload.itemStatus,
          payload.itemQuantity ?? 1
        );
      }

      const nextStatus = payload.orderStatus || payload.status;
      if (!nextStatus) return;

      const currentOrder = ongoingOrderRef.current;
      const name = restaurantNameRef.current;
      const payloadOrderCode = payload.orderCode ?? undefined;
      const baseSnapshot: OngoingOrderSnapshot = currentOrder ?? {
        orderId: trackedOrderId,
        orderStatus: nextStatus,
        orderType: DINE_IN_ORDER_TYPE,
        orderCode: payloadOrderCode,
        items: [],
      };
      const waitMinutes =
        payload.approxWaitTime != null && payload.approxWaitTime > 0
          ? payload.approxWaitTime
          : baseSnapshot.waitTimeMinutes;
      const snapshotWithWait = {
        ...baseSnapshot,
        orderStatus: nextStatus,
        waitTimeMinutes: waitMinutes,
        orderCode: baseSnapshot.orderCode ?? payloadOrderCode,
      };

      if (isTerminalOrderStatus(nextStatus)) {
        if (name) {
          pushStatusIfChanged(nextStatus, snapshotWithWait, name);
        }
        clearOngoingOrderContext();
        setOngoingOrder(null);
        setPendingOrderId(null);
        lastStatusRef.current = null;
        return;
      }

      if (nextStatus === 'ORDER_MODIFIED') {
        pushStatusIfChanged(nextStatus, snapshotWithWait, name);
        loadOngoingOrder();
        return;
      }

      pushStatusIfChanged(nextStatus, snapshotWithWait, name);
      setOngoingOrder((prev) => {
        const next = prev ?? baseSnapshot;
        return { ...next, orderStatus: nextStatus, waitTimeMinutes: waitMinutes };
      });
    });
  }, [trackedOrderId, subscribeOrderStatus, pushStatusIfChanged, pushItemStatusIfChanged, loadOngoingOrder]);

  useEffect(() => {
    if (!trackedOrderId) return undefined;
    return subscribeOrderWaitTime((payload) => {
      if (Number(payload.orderId) !== trackedOrderId) return;
      const minutes = payload.approxWaitTime;
      if (!minutes || minutes <= 0) return;
      if (hasAnnouncedWaitTime(trackedOrderId, minutes)) return;

      setOngoingOrder((prev) => {
        const base = prev ?? {
          orderId: trackedOrderId,
          orderStatus: 'ORDER_PREPARING',
          orderType: DINE_IN_ORDER_TYPE,
          items: [],
        };
        return { ...base, waitTimeMinutes: minutes };
      });

      markAnnouncedWaitTime(trackedOrderId, minutes);
      const { message, speakText } = resolveWaitTimeMessage(minutes, trackedOrderId);
      pushMessage(message, speakText);
    });
  }, [trackedOrderId, subscribeOrderWaitTime, pushMessage]);

  useEffect(() => {
    return subscribeTableRequestStatus((payload) => {
      if (!payload.approved) return;
      const targetApp = appName ?? resolveAppName();
      const { message, speakText } = resolveTableReadyMessage();
      pushMessage(message, speakText);
      if (targetApp) {
        void restaurantService.getDineInTableStatus(targetApp).then((res) => {
          const tableNumber = res.dineInTable?.tableNumber;
          if (tableNumber != null) {
            setTableAllocated(true);
            setAllocatedTableNumber(Number(tableNumber));
            notifyCaptainTableApproved(Number(tableNumber));
          } else {
            notifyCaptainTableApproved(0);
          }
        });
      }
    });
  }, [subscribeTableRequestStatus, pushMessage, appName, resolveAppName]);

  useEffect(() => {
    return subscribeQueueTokenUpdates((payload) => {
      if (!payload.tokenLabel) return;
      notifyCaptainQueueUpdated(payload);
      if (payload.status === 'READY') {
        const { message, speakText } = resolveQueueReadyMessage(payload.tokenLabel);
        pushMessage(message, speakText);
        return;
      }
      if (payload.status === 'CALLING' || payload.status === 'WAITING') {
        const { message, speakText } = resolveQueueStatusMessage(
          payload.tokenLabel,
          payload.status,
          payload.position
        );
        pushMessage(message, speakText);
      }
    });
  }, [subscribeQueueTokenUpdates, pushMessage]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ orderId?: number }>).detail;
      if (detail?.orderId != null) {
        setPendingOrderId(Number(detail.orderId));
        displayedOrderStatusRef.current = null;
        lastStatusRef.current = null;
      }
      void loadOngoingOrder();
    };
    window.addEventListener(ORDER_PLACED_EVENT, handler);
    return () => window.removeEventListener(ORDER_PLACED_EVENT, handler);
  }, [loadOngoingOrder]);

  const submitServiceRequest = useCallback(
    async (service: string, label: string, orderSnapshot?: OngoingOrderSnapshot): Promise<boolean> => {
      const targetOrder = orderSnapshot ?? ongoingOrder;
      const targetApp = appName ?? resolveAppName();
      if (!targetApp) {
        pushMessage({
          id: `assist-error-${Date.now()}`,
          type: 'system',
          text: 'Could not send request. Refresh the page and try again.',
          time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
        });
        return false;
      }
      try {
        let response: { success: boolean; message?: string };
        if (targetOrder?.orderId) {
          // Post-order path: use order-bound service request
          response = await restaurantService.submitUserServiceRequest(
            targetApp,
            targetOrder.orderId,
            service
          );
        } else {
          // Pre-order path: table-level service request (no order yet)
          response = await restaurantService.submitTableAssistance(targetApp, service);
        }
        if (response.success) {
          const tableNum = targetOrder?.tableNumber ?? allocatedTableNumber;
          const { message, speakText } = resolveAssistanceMessage(label, tableNum);
          pushMessage(message, speakText);
          return true;
        }
        pushMessage({
          id: `assist-error-${Date.now()}`,
          type: 'system',
          text: response.message || `Could not send ${label} request. Please try again.`,
          time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
        });
      } catch {
        pushMessage({
          id: `assist-error-${Date.now()}`,
          type: 'system',
          text: `Could not send ${label} request. Check your connection and try again.`,
          time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
        });
      }
      return false;
    },
    [appName, ongoingOrder, allocatedTableNumber, pushMessage, resolveAppName]
  );

  const callCaptain = useCallback(async (orderSnapshot?: OngoingOrderSnapshot): Promise<boolean> => {
    const targetOrder = orderSnapshot ?? ongoingOrder;
    const targetApp = appName ?? resolveAppName();
    if (!targetApp) {
      pushMessage({
        id: `captain-error-${Date.now()}`,
        type: 'system',
        text: 'Could not call captain. Refresh the page and try again.',
        time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
      });
      return false;
    }
    try {
      let response: { success: boolean; message?: string };
      if (targetOrder?.orderId) {
        response = await restaurantService.submitUserServiceRequest(
          targetApp,
          targetOrder.orderId,
          'CALL_WAITER'
        );
      } else {
        response = await restaurantService.submitTableAssistance(targetApp, 'CALL_WAITER');
      }
      if (response.success) {
        const tableNum = targetOrder?.tableNumber ?? allocatedTableNumber;
        const { message, speakText } = resolveCallCaptainMessage(tableNum);
        pushMessage(message, speakText);
        return true;
      }
      pushMessage({
        id: `captain-error-${Date.now()}`,
        type: 'system',
        text: response.message || 'Could not call captain. Please try again.',
        time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
      });
    } catch {
      pushMessage({
        id: `captain-error-${Date.now()}`,
        type: 'system',
        text: 'Could not call captain. Check your connection and try again.',
        time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
      });
    }
    return false;
  }, [appName, ongoingOrder, allocatedTableNumber, pushMessage, resolveAppName]);

  const latestMessage = messages.length > 0 ? messages[messages.length - 1] : null;

  const dismissVoiceTip = useCallback(() => {
    setShowVoiceTip(false);
    markVoiceTipDismissed();
  }, []);

  const enableVoiceFromTip = useCallback(() => {
    setVoiceEnabled(true);
    dismissVoiceTip();
  }, [setVoiceEnabled, dismissVoiceTip]);

  const contextValue = useMemo(
    () => ({
      active,
      assistanceEnabled,
      tableAllocated,
      allocatedTableNumber,
      restaurantName,
      messages,
      latestMessage,
      ongoingOrder,
      voiceEnabled,
      setVoiceEnabled,
      unlockVoice,
      showVoiceTip,
      setShowVoiceTip: (show: boolean) => {
        setShowVoiceTip(show);
        if (!show) markVoiceTipDismissed();
      },
      dismissVoiceTip,
      enableVoiceFromTip,
      pushMessage,
      submitServiceRequest,
      callCaptain,
      assistanceAvailable,
      chatEnabled,
      chatMessages,
      streamingText,
      streamingMenuItems,
      streamingTableItems,
      streamingDealItems,
      chatConnected,
      chatConnectionFailed,
      sendChatMessage: sendUserMessage,
      submitChoiceSelection,
      addMenuItemToCart,
      appName,
      restaurantId: restaurantId ? Number(restaurantId) : undefined,
    }),
    [
      active,
      assistanceEnabled,
      tableAllocated,
      allocatedTableNumber,
      restaurantName,
      messages,
      latestMessage,
      ongoingOrder,
      voiceEnabled,
      setVoiceEnabled,
      unlockVoice,
      showVoiceTip,
      dismissVoiceTip,
      enableVoiceFromTip,
      pushMessage,
      submitServiceRequest,
      callCaptain,
      assistanceAvailable,
      chatEnabled,
      chatMessages,
      streamingText,
      streamingMenuItems,
      streamingTableItems,
      streamingDealItems,
      chatConnected,
      chatConnectionFailed,
      sendUserMessage,
      submitChoiceSelection,
      addMenuItemToCart,
      appName,
      restaurantId,
    ]
  );

  if (!widgetActive) {
    return null;
  }

  return (
    <VirtualCaptainProvider value={contextValue}>
      <CaptainWidget />
    </VirtualCaptainProvider>
  );
};

export default MaznutCaptainBridge;
